import { forwardRef, useEffect, useState } from "react";
import classes from "./CompactTimeInput.module.scss";
import { TextInput } from "@vfuk/core-text-input/dist/TextInput";
import { TimeInputFormat } from "../TimeInput/TimeInput";
import type { TimeInputState } from "../TimeInput/types";
import { FloatingLabelInput } from "../FloatingLabelInput";

const timeFormatHM = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9])$/;
const timeFormatHMS = /^(2[0-3]|[01]?[0-9]):([0-5]?[0-9]):([0-5]?[0-9])$/;
const MAX_HOURS = 24;
const MAX_MINS = 60;
const DEFAULT_INPUTTED_ZERO = "0";
const FORMATTED_ZERO = "00";
const timeRegExpHM = new RegExp(timeFormatHM);
const timeRegexpHMS = new RegExp(timeFormatHMS);

interface DisplayTime {
  hours: string;
  minutes: string;
  seconds: string;
}

type CompactTimeInputProps = {
  onChange: (value: TimeInputFormat) => void;
  onBlur?: () => void;
  value: TimeInputFormat;
  name: string;
  error?: boolean;
  state?: TimeInputState;
  label: string;
  spaceForError?: boolean;
  showSeconds?: boolean;
};

const CompactTimeInput = forwardRef<TextInput, CompactTimeInputProps>(
  (
    {
      onChange,
      onBlur,
      value,
      name,
      error = false,
      state,
      label,
      spaceForError = false,
      showSeconds = false,
    }: CompactTimeInputProps,
    ref
  ): JSX.Element => {
    const timeRegExp = showSeconds ? timeRegexpHMS : timeRegExpHM;
    // NB - valueInProgress is initially set via a useEffect (because the parent
    // can change the values at any time).
    const [valueInProgress, setValueInProgress] = useState<DisplayTime>({
      hours: "",
      minutes: "",
      seconds: "",
    });

    const prependZero = (number: string): string => {
      if (number.length === 0) return "00";
      if (number.length === 1) return `0${number}`;
      // It must be  length >= 2
      return number;
    };

    const onFloatingLabelInputBlur = () => {
      const displayedTime = [
        prependZero(valueInProgress.hours),
        prependZero(valueInProgress.minutes),
        prependZero(valueInProgress.seconds || DEFAULT_INPUTTED_ZERO),
      ].join(":");
      const hoursInt = parseInt(valueInProgress.hours);
      const minsInt = parseInt(valueInProgress.minutes);
      const secsInt = parseInt(valueInProgress.seconds);
      const isPatternMatch = displayedTime.match(timeRegExp) !== null;
      if (
        isPatternMatch &&
        hoursInt >= 0 &&
        hoursInt < MAX_HOURS &&
        minsInt >= 0 &&
        minsInt < MAX_MINS
      ) {
        // valid entry - send the value onChange
        const newHours = prependZero(valueInProgress.hours);
        const newMins = prependZero(valueInProgress.minutes);
        const newSecs = secsInt
          ? prependZero(valueInProgress.seconds || DEFAULT_INPUTTED_ZERO)
          : FORMATTED_ZERO;
        // NB - we don't display ms, but do send it.
        onChange({
          hours: newHours,
          minutes: newMins,
          seconds: newSecs,
          milliseconds: value.milliseconds || DEFAULT_INPUTTED_ZERO,
        });
        setValueInProgress({
          hours: newHours,
          minutes: newMins,
          seconds: newSecs,
        });
      } else {
        // Invalid entry - return back to initial value.
        setValueInProgress({
          hours: value.hours || "",
          minutes: value.minutes || "",
          seconds: value.seconds || "",
        });
      }
    };

    let timeoutId: ReturnType<typeof setTimeout>;
    const onFieldsetBlur = () => {
      timeoutId = setTimeout(() => {
        onBlur?.();
      }, 5);
    };

    const onFieldsetFocus = () => {
      clearTimeout(timeoutId);
    };

    const validateInput = (input: string) => {
      const splitInput = input.split(":");
      setValueInProgress({
        hours: splitInput[0] || "",
        minutes: splitInput[1] || "",
        seconds: splitInput[2] || "",
      });
    };

    const createDisplayTime = ({ hours, minutes, seconds }: DisplayTime) => {
      return [hours, minutes, seconds].join(":");
    };

    useEffect(() => {
      // The time value has changed from outside the component...
      setValueInProgress({
        hours: value.hours || "",
        minutes: value.minutes || "",
        seconds: value.seconds || "",
      });
    }, [value]);

    return (
      <fieldset
        className={classes.CompactTimeInputContainer}
        onFocus={onFieldsetFocus}
        onBlur={onFieldsetBlur}
      >
        <FloatingLabelInput
          label={label}
          ref={ref}
          id={`${name}-hour`}
          name={`${name}-hour`}
          dataAttributes={{ "aria-label": `${name} hour` }}
          type="text"
          maxLength={8}
          value={createDisplayTime(valueInProgress)}
          state={error ? "error" : state}
          onChange={(event) => {
            validateInput(event.target.value);
          }}
          onBlur={onFloatingLabelInputBlur}
          spaceForError={spaceForError}
        />
      </fieldset>
    );
  }
);

CompactTimeInput.displayName = "CompactTimeInput";

export default CompactTimeInput;
